import { Cancel, Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Checkbox, IconButton, InputAdornment, Modal, Paper, TextField, Typography } from "@mui/material";
import { Fragment, ReactNode, useState } from "react";
import { GobikesApi } from "../../config/Apis";
import { getAuthCookie } from "../auth/AuthUtil";
import { BookingType, OrderModel } from "../utils/Models";

type PaymentCheckbox = 'partial' | 'security_deposit' | 'outstanding';

const CHECKBOX_LABELS: Record<PaymentCheckbox, string> = {
  partial: 'Partial Payment',
  security_deposit: 'Security Deposit',
  outstanding: 'Outstanding Amount'
};

function deriveAutoRemark(selected: PaymentCheckbox[]): string {
  return selected.map(k => CHECKBOX_LABELS[k]).join(' + ');
}

interface FormState {
  loading?: boolean;
  error?: boolean;
  message?: string;
}

export function CreateCustomPaymentLink(props: {
  children: ReactNode;
  order: OrderModel;
  style?: any;
  backFn: () => void;
}) {
  const isSubscriptionOrder = props.order.bookingType === BookingType.SUBSCRIPTION;

  const [open, setOpen]           = useState(false);
  const [isPartial, setIsPartial] = useState(false);
  const [isDeposit, setIsDeposit] = useState(false);
  const [isOutstanding, setIsOutstanding] = useState(false);
  const [amount, setAmount]       = useState<number>(0);
  const [remark, setRemark]       = useState<string>('');
  const [remarkIsAuto, setRemarkIsAuto] = useState(true);
  const [formState, setFormState] = useState<FormState>({});

  const authCookie = getAuthCookie();

  function handleOpen() {
    setIsPartial(false);
    setIsDeposit(false);
    setIsOutstanding(false);
    setAmount(0);
    setRemark('');
    setRemarkIsAuto(true);
    setFormState({});
    setOpen(true);
  }

  function handleClose() { setOpen(false); }

  function deriveAmount(partial: boolean, deposit: boolean, outstanding: boolean): number {
    return (partial ? (props.order.offlineCashAmount || 0) : 0)
         + (deposit ? (props.order.depositAmount || 0) : 0)
         + (outstanding ? (props.order.outstandingAmount || 0) : 0);
  }

  function applyCheckboxChange(partial: boolean, deposit: boolean, outstanding: boolean) {
    setIsPartial(partial);
    setIsDeposit(deposit);
    setIsOutstanding(outstanding);
    setAmount(deriveAmount(partial, deposit, outstanding));
    if (remarkIsAuto) {
      const selected: PaymentCheckbox[] = [];
      if (partial) selected.push('partial');
      if (deposit) selected.push('security_deposit');
      if (outstanding) selected.push('outstanding');
      setRemark(deriveAutoRemark(selected));
    }
  }

  function handlePartialChange(checked: boolean) { applyCheckboxChange(checked, isDeposit, isOutstanding); }
  function handleDepositChange(checked: boolean) { applyCheckboxChange(isPartial, checked, isOutstanding); }
  function handleOutstandingChange(checked: boolean) { applyCheckboxChange(isPartial, isDeposit, checked); }

  async function handleSubmit(e: any) {
    e.preventDefault();

    if (!isPartial && !isDeposit && !isOutstanding) {
      setFormState({ error: true, message: 'Select at least one payment type' });
      return;
    }
    if (!amount || amount <= 0) {
      setFormState({ error: true, message: 'Enter a valid amount' });
      return;
    }
    if (!remark || !remark.trim()) {
      setFormState({ error: true, message: 'Enter a remark' });
      return;
    }

    const paymentTypes: PaymentCheckbox[] = [];
    if (isPartial) paymentTypes.push('partial');
    if (isDeposit) paymentTypes.push('security_deposit');
    if (isOutstanding) paymentTypes.push('outstanding');

    setFormState({ loading: true });
    try {
      await GobikesApi.post(
        `/vendors/hubs/${props.order.hubId}/orders/${props.order.id}/custom-payment-link`,
        { amount, remark: remark.trim(), paymentTypes, ...(isOutstanding && { outstandingPortion: props.order.outstandingAmount || 0 }) },
        { headers: { 'vendor-token': authCookie } }
      );
      setFormState({ error: false, message: 'Payment link created!' });
      setTimeout(() => { handleClose(); props.backFn(); }, 2000);
    } catch (e: any) {
      const errorMessage = (e.response?.data?.message) ? 'Error: ' + e.response.data.message : 'An error occurred';
      setFormState({ error: true, message: errorMessage, loading: false });
    }
  }

  return (
    <Fragment>
      <span onClick={handleOpen} style={props.style}>
        {props.children}
      </span>
      <Modal open={open} className="gbk-modal" onClose={handleClose}>
        <Box className="gbk-modal-content" style={{ width: 'min(380px, calc(100vw - 32px))', height: 'auto', maxHeight: 'calc(100vh - 80px)', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'absolute', overflowY: 'auto' }}>
          <Paper className="gbk-paper" sx={{ borderRadius: '16px !important', overflow: 'hidden', height: 'auto !important' }}>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>Custom Payment Link</Typography>
              <IconButton onClick={handleClose} size="small" sx={{ color: '#666' }}>
                <Close fontSize="small" />
              </IconButton>
            </Box>

            {/* Form */}
            <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>

              {/* Payment type label + charge note */}
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                Payment Type
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#e07b00', background: '#fff8ee', border: '1px solid #ffe0a0', borderRadius: '8px', px: 1.2, py: 0.6, mb: 1.5, display: 'block' }}>
                2% payment charges will be deducted from this payment
              </Typography>

              {/* Checkboxes in a row */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Box
                  onClick={() => handlePartialChange(!isPartial)}
                  sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, border: isPartial ? '1.5px solid #8dbe2e' : '1.5px solid #e0e0e0', borderRadius: '10px', px: 1.5, py: 1, cursor: 'pointer', background: isPartial ? '#f4ffe0' : '#fafafa', transition: 'all 0.15s' }}
                >
                  <Checkbox size="small" checked={isPartial} onChange={(e) => handlePartialChange(e.target.checked)} sx={{ p: 0, mr: 0.5 }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, userSelect: 'none' }}>Partial Payment</Typography>
                </Box>
                <Box
                  onClick={() => handleDepositChange(!isDeposit)}
                  sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, border: isDeposit ? '1.5px solid #8dbe2e' : '1.5px solid #e0e0e0', borderRadius: '10px', px: 1.5, py: 1, cursor: 'pointer', background: isDeposit ? '#f4ffe0' : '#fafafa', transition: 'all 0.15s' }}
                >
                  <Checkbox size="small" checked={isDeposit} onChange={(e) => handleDepositChange(e.target.checked)} sx={{ p: 0, mr: 0.5 }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, userSelect: 'none' }}>Security Deposit</Typography>
                </Box>
                {isSubscriptionOrder && (
                  <Box
                    onClick={() => handleOutstandingChange(!isOutstanding)}
                    sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, border: isOutstanding ? '1.5px solid #8dbe2e' : '1.5px solid #e0e0e0', borderRadius: '10px', px: 1.5, py: 1, cursor: 'pointer', background: isOutstanding ? '#f4ffe0' : '#fafafa', transition: 'all 0.15s' }}
                  >
                    <Checkbox size="small" checked={isOutstanding} onChange={(e) => handleOutstandingChange(e.target.checked)} sx={{ p: 0, mr: 0.5 }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, userSelect: 'none' }}>Outstanding Amount</Typography>
                  </Box>
                )}
              </Box>

              {/* Amount */}
              <TextField
                label="Amount (₹)"
                type="number"
                size="small"
                fullWidth
                value={amount || ''}
                onChange={(e) => setAmount(Number(e.target.value))}
                InputProps={{
                  endAdornment: amount > 0 ? (
                    <InputAdornment position="end">
                      <Cancel htmlColor="#e74c3c" style={{ cursor: 'pointer', fontSize: 18 }} onClick={() => setAmount(0)} />
                    </InputAdornment>
                  ) : undefined
                }}
                sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '14px' }, '& label': { fontSize: '13px' } }}
              />

              {/* Remark */}
              <TextField
                label="Remark"
                size="small"
                fullWidth
                value={remark}
                onChange={(e) => { setRemark(e.target.value); setRemarkIsAuto(false); }}
                sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '14px' }, '& label': { fontSize: '13px' } }}
              />

              {formState.message && (
                <Typography sx={{ fontSize: '12px', color: formState.error ? '#c0392b' : '#27ae60', mt: 0.5, mb: 0.5 }}>
                  {formState.message}
                </Typography>
              )}
            </Box>

            {/* Footer */}
            <Box sx={{ px: 2.5, pb: 2.5 }}>
              <form onSubmit={handleSubmit}>
                <LoadingButton
                  variant="contained"
                  type="submit"
                  fullWidth
                  loading={formState.loading}
                  sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, fontSize: '14px', py: 1.2, background: '#8dbe2e', '&:hover': { background: '#78a225' } }}
                >
                  Create
                </LoadingButton>
              </form>
            </Box>

          </Paper>
        </Box>
      </Modal>
    </Fragment>
  );
}
